import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Collapse,
  Descriptions,
  Empty,
  Space,
  Tag,
  Typography,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  formatDocumentUiDate,
  formatDocumentUiTime,
} from '../../../utils/documentUiFormat';
import DataTable from '../../../ui/Table/DataTable';
import { useGetClosingPackageJournalQuery } from '../api/closingPackageApi';
import {
  buildClosingPackageJournalDisplayEntries,
  type ClosingPackageJournalDetail,
  type ClosingPackageJournalDisplayEntry,
} from '../lib/closingPackageJournalDisplay';
import type { ClosingPackageItem } from '../types';

const { Text } = Typography;

export type ClosingPackageJournalPanelProps = {
  applicationId: number;
  items: ClosingPackageItem[];
};

function hasJournalKey(keys: string | string[]): boolean {
  return Array.isArray(keys) ? keys.includes('journal') : keys === 'journal';
}

const ClosingPackageJournalPanel: React.FC<ClosingPackageJournalPanelProps> = ({
  applicationId,
  items,
}) => {
  const [enabled, setEnabled] = useState(false);
  const { data, isFetching, isError, refetch } = useGetClosingPackageJournalQuery(
    applicationId,
    { skip: !enabled }
  );

  const itemLabels = useMemo(
    () =>
      Object.fromEntries(items.map((item) => [item.key, item.label])) as Record<
        string,
        string
      >,
    [items]
  );

  const rows = useMemo(
    () => buildClosingPackageJournalDisplayEntries(data?.entries ?? [], itemLabels),
    [data?.entries, itemLabels]
  );

  const columns: ColumnsType<ClosingPackageJournalDisplayEntry> = useMemo(
    () => [
      {
        title: 'Время',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 145,
        render: (value: string) => (
          <Space direction="vertical" size={0}>
            <Text>{formatDocumentUiDate(value)}</Text>
            <Text type="secondary" className="cp-journal__timestamp">
              {formatDocumentUiTime(value)}
            </Text>
          </Space>
        ),
      },
      {
        title: 'Пользователь',
        dataIndex: 'actorLabel',
        key: 'actorLabel',
        width: 170,
        ellipsis: true,
        render: (value: string) => <Text ellipsis={{ tooltip: value }}>{value}</Text>,
      },
      {
        title: 'Действие',
        dataIndex: 'actionLabel',
        key: 'actionLabel',
        width: 205,
        render: (_value: unknown, record: ClosingPackageJournalDisplayEntry) => (
          <Tag color={record.actionColor} className="cp-journal__action-tag">
            {record.actionLabel}
          </Tag>
        ),
      },
      {
        title: 'Описание',
        dataIndex: 'description',
        key: 'description',
        width: 440,
        render: (_value: unknown, record: ClosingPackageJournalDisplayEntry) => (
          <Space direction="vertical" size={2} className="cp-journal__description">
            <Text>{record.description}</Text>
            {record.details.length > 0 ? (
              <Collapse
                ghost
                size="small"
                className="cp-journal__details"
                items={[
                  {
                    key: 'details',
                    label: <Text type="secondary">Подробнее</Text>,
                    children: (
                      <Descriptions
                        size="small"
                        column={1}
                        items={record.details.map((detail: ClosingPackageJournalDetail) => ({
                          key: detail.label,
                          label: detail.label,
                          children: (
                            <pre className="cp-journal__detail-value">{detail.value}</pre>
                          ),
                        }))}
                      />
                    ),
                  },
                ]}
              />
            ) : null}
          </Space>
        ),
      },
      {
        title: 'Результат',
        dataIndex: 'resultLabel',
        key: 'resultLabel',
        width: 105,
        align: 'center',
        render: (_value: unknown, record: ClosingPackageJournalDisplayEntry) => (
          <Tag color={record.resultColor}>{record.resultLabel}</Tag>
        ),
      },
    ],
    []
  );

  const content = isError ? (
    <Alert
      type="error"
      showIcon
      message="Не удалось загрузить журнал комплекта"
      action={
        <Button
          size="small"
          icon={<ReloadOutlined />}
          onClick={() => {
            void refetch();
          }}
        >
          Повторить
        </Button>
      }
    />
  ) : (
    <DataTable<ClosingPackageJournalDisplayEntry>
      size="small"
      rowKey="id"
      columns={columns}
      dataSource={rows}
      loading={isFetching}
      pagination={false}
      tableLayout="fixed"
      scroll={{ x: 1065, y: 240 }}
      className="cp-journal__table"
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Операции с комплектом пока не зафиксированы"
          />
        ),
      }}
    />
  );

  return (
    <Collapse
      className="cp-journal cp-span-full"
      onChange={(keys) => {
        if (hasJournalKey(keys)) {
          setEnabled(true);
        }
      }}
      items={[
        {
          key: 'journal',
          label: (
            <Space>
              <Text strong>Журнал операций состава</Text>
              {data ? <Text type="secondary">{data.entries.length}</Text> : null}
            </Space>
          ),
          children: content,
        },
      ]}
    />
  );
};

export default ClosingPackageJournalPanel;
